import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  fetchSentEmails,
  sendEmail as sendEmailAction,
  sendEmailWithTemplate as sendEmailWithTemplateAction,
} from '../../../controllers/emailController';
import { searchEmailTemplates, fetchEmailTemplateById } from '../../../controllers/emailTemplateController';
import RichTextEditor from '../../../components/RichTextEditor';
import { fetchUserById } from '../../../controllers/userManagementController';

const defaultPagination = {
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
};

const parseEmails = (value) =>
  String(value || '')
    .split(/[,;\n]+/)
    .map((v) => v.trim())
    .filter(Boolean);

const resolveField = (item, keys = []) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }
  return undefined;
};

const EmailSendRecordsTab = () => {
  const [mode, setMode] = useState('manual');

  const [manualForm, setManualForm] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
  });

  const [templateForm, setTemplateForm] = useState({
    to: '',
    cc: '',
    bcc: '',
    template: null,
    subjectOverride: '',
  });
  const [templateQuery, setTemplateQuery] = useState('');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  const [records, setRecords] = useState([]);
  const [recordsMeta, setRecordsMeta] = useState(defaultPagination);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailTemplate, setDetailTemplate] = useState(null);
  const [detailSender, setDetailSender] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [templateNameMap, setTemplateNameMap] = useState({});
  const templateNameCacheRef = useRef({});

  const loadTemplateOptions = useCallback(
    async (keyword) => {
      if (!keyword || keyword.length < 2) {
        setTemplateOptions([]);
        return;
      }
      setTemplateLoading(true);
      try {
        const data = await searchEmailTemplates({ keyword });
        setTemplateOptions(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
      } catch (error) {
        setTemplateOptions([]);
      } finally {
        setTemplateLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadTemplateOptions(templateQuery);
  }, [templateQuery, loadTemplateOptions]);

  useEffect(() => {
    templateNameCacheRef.current = templateNameMap;
  }, [templateNameMap]);

  const loadRecords = useCallback(
    async ({ pageNumber, pageSize, keyword }) => {
      setRecordsLoading(true);
      setRecordsError('');
      try {
        const { data, meta } = await fetchSentEmails({ pageNumber, pageSize, search: keyword });
        const recordsArray = Array.isArray(data) ? data : [];
        setRecords(recordsArray);
        setRecordsMeta({
          pageNumber: meta?.currentPage ?? pageNumber,
          pageSize: meta?.pageSize ?? pageSize,
          totalCount: meta?.totalCount ?? recordsArray.length,
          totalPages:
            (meta?.totalPages ?? Math.ceil((meta?.totalCount ?? 0) / (meta?.pageSize ?? pageSize))) || 1,
          hasNext: meta?.hasNext ?? false,
          hasPrevious: meta?.hasPrevious ?? false,
        });

        const templateIdsToFetch = recordsArray
          .map((record) => record.templateId || record.TemplateId)
          .filter(Boolean)
          .filter((id, index, arr) => arr.indexOf(id) === index)
          .filter((id) => !templateNameCacheRef.current[id]);

        if (templateIdsToFetch.length) {
          const fetchedNames = {};
          await Promise.all(
            templateIdsToFetch.map(async (id) => {
              try {
                const template = await fetchEmailTemplateById(id);
                fetchedNames[id] = template?.name || template?.Name || '—';
              } catch (err) {
                fetchedNames[id] = '—';
              }
            })
          );
          if (Object.keys(fetchedNames).length) {
            templateNameCacheRef.current = { ...templateNameCacheRef.current, ...fetchedNames };
            setTemplateNameMap((prev) => ({ ...prev, ...fetchedNames }));
          }
        }
      } catch (error) {
        setRecords([]);
        setRecordsMeta(defaultPagination);
        setRecordsError(error?.message || 'Gönderim kayıtları alınamadı');
      } finally {
        setRecordsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadRecords({ pageNumber: page + 1, pageSize: rowsPerPage, keyword: search || undefined });
  }, [page, rowsPerPage, reloadKey, search, loadRecords]);

  const resetManualForm = () => {
    setManualForm({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
    });
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      to: '',
      cc: '',
      bcc: '',
      template: null,
      subjectOverride: '',
    });
    setTemplateQuery('');
    setTemplateOptions([]);
  };

  const validateManualForm = () => {
    const toList = parseEmails(manualForm.to);
    if (!toList.length) {
      setSendError('En az bir alıcı (To) adresi girmelisiniz.');
      return false;
    }
    if (!manualForm.subject.trim()) {
      setSendError('Konu alanı boş olamaz.');
      return false;
    }
    if (!manualForm.body || !manualForm.body.trim()) {
      setSendError('İçerik alanı boş olamaz.');
      return false;
    }
    return true;
  };

  const validateTemplateForm = () => {
    const toList = parseEmails(templateForm.to);
    if (!toList.length) {
      setSendError('En az bir alıcı (To) adresi girmelisiniz.');
      return false;
    }
    if (!templateForm.template) {
      setSendError('Gönderim için bir e-posta şablonu seçmelisiniz.');
      return false;
    }
    return true;
  };

  const handleSendManual = async () => {
    if (!validateManualForm()) return;
    setSending(true);
    setSendError('');
    setSendSuccess('');
    try {
      const payload = {
        to: parseEmails(manualForm.to),
        cc: parseEmails(manualForm.cc),
        bcc: parseEmails(manualForm.bcc),
        subject: manualForm.subject.trim(),
        body: manualForm.body,
        isHtml: true,
      };
      await sendEmailAction(payload);
      setSendSuccess('E-posta başarıyla gönderildi.');
      resetManualForm();
      setReloadKey((key) => key + 1);
    } catch (error) {
      setSendError(error?.message || 'E-posta gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!validateTemplateForm()) return;
    setSending(true);
    setSendError('');
    setSendSuccess('');
    try {
      const payload = {
        templateId: templateForm.template?.id || templateForm.template?.Id,
        to: parseEmails(templateForm.to),
        cc: parseEmails(templateForm.cc),
        bcc: parseEmails(templateForm.bcc),
        subjectOverride: templateForm.subjectOverride?.trim() || null,
      };
      await sendEmailWithTemplateAction(payload);
      setSendSuccess('Şablon ile e-posta başarıyla gönderildi.');
      resetTemplateForm();
      setReloadKey((key) => key + 1);
    } catch (error) {
      setSendError(error?.message || 'Şablon ile e-posta gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  const recipientsHelper = 'Birden fazla adresi virgülle veya satır sonu ile ayırabilirsiniz.';

  const handleRefresh = () => {
    loadRecords({ pageNumber: page + 1, pageSize: rowsPerPage, keyword: search || undefined });
  };

  const openDetail = async (record) => {
    if (!record) return;
    const templateId = record.templateId || record.TemplateId;
    const senderId = record.sentById || record.SentById || record.sentBy || record.SentBy;

    setDetailRecord(record);
    setDetailTemplate(null);
    setDetailSender(null);
    setDetailError('');
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      if (templateId) {
        const template = await fetchEmailTemplateById(templateId);
        setDetailTemplate(template);
        if (template) {
          const templateName = template.name || template.Name || '—';
          templateNameCacheRef.current = {
            ...templateNameCacheRef.current,
            [templateId]: templateName,
          };
          setTemplateNameMap((prev) => ({
            ...prev,
            [templateId]: templateName,
          }));
        }
      }
      if (senderId) {
        const sender = await fetchUserById(senderId);
        setDetailSender(sender);
      }
    } catch (err) {
      setDetailError(err?.message || 'Detaylar alınamadı');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRecord(null);
    setDetailTemplate(null);
    setDetailSender(null);
    setDetailError('');
  };

  const renderHistoryTable = () => {
    if (recordsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (recordsError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{recordsError}</Alert>
        </Box>
      );
    }
    if (!records.length) {
      return (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          Henüz gönderim kaydı bulunmuyor.
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Konu</TableCell>
              <TableCell>Alıcılar</TableCell>
              <TableCell>Şablon</TableCell>
              <TableCell>Kaynak</TableCell>
              <TableCell>Gönderim</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right">Detay</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record) => {
              const key = record.id || record.Id || record.recordId || record.RecordId;
              const subject =
                resolveField(record, ['subject', 'Subject', 'subjectOverride', 'SubjectOverride']) || '—';
              const toField = resolveField(record, ['to', 'To', 'recipients', 'Recipients']);
              const templateId = resolveField(record, ['templateId', 'TemplateId']);
              const templateName =
                (templateId && templateNameMap[templateId]) ||
                resolveField(record, ['templateName', 'TemplateName', 'template', 'Template']) ||
                (templateId ? 'Yükleniyor...' : '—');
              const statusField = resolveField(record, [
                'status',
                'Status',
                'result',
                'Result',
                'isSuccess',
                'IsSuccess',
                'success',
                'Success',
              ]);
              const sentDate = resolveField(record, ['sentAt', 'SentAt', 'createdAt', 'CreatedAt', 'updatedAt', 'UpdatedAt']);
              const source = resolveField(record, ['source', 'Source']) || '—';
              const errorMessage =
                resolveField(record, ['error', 'Error', 'errorMessage', 'ErrorMessage', 'failureReason', 'FailureReason']) ||
                null;

              const toValue = Array.isArray(toField)
                ? toField.join(', ')
                : typeof toField === 'string'
                ? toField
                : '—';

              let statusNode = 'Başarılı';
              if (typeof statusField === 'string' && statusField.trim()) {
                statusNode = statusField;
              } else if (typeof statusField === 'boolean') {
                statusNode = statusField ? 'Başarılı' : 'Hatalı';
              } else if (errorMessage) {
                statusNode = 'Hatalı';
              }

              const sentLabel = sentDate ? new Date(sentDate).toLocaleString('tr-TR') : '—';

              return (
                <TableRow key={key || subject + sentLabel}>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 600 }}>{subject}</Typography>
                      {templateName && templateName !== '—' && (
                        <Typography variant="caption" color="text.secondary">
                          Şablon: {templateName}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{toValue}</Typography>
                  </TableCell>
                  <TableCell>{templateName}</TableCell>
                  <TableCell>{source}</TableCell>
                  <TableCell>{sentLabel}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusNode === 'Başarılı' || String(statusNode).toLowerCase() === 'success' ? 'success' : statusNode === 'Hatalı' ? 'error' : 'default'}
                      label={statusNode}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button variant="outlined" size="small" onClick={() => openDetail(record)}>
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Tabs
            value={mode}
            onChange={(event, value) => {
              setMode(value);
              setSendError('');
              setSendSuccess('');
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Doğrudan E-Posta" value="manual" />
            <Tab label="Şablon ile Gönder" value="template" />
          </Tabs>

          {sendError && <Alert severity="error">{sendError}</Alert>}
          {sendSuccess && <Alert severity="success">{sendSuccess}</Alert>}

          {mode === 'manual' ? (
            <Stack spacing={1.5}>
              <TextField
                label="Alıcılar (To)"
                value={manualForm.to}
                onChange={(e) => setManualForm((prev) => ({ ...prev, to: e.target.value }))}
                helperText={recipientsHelper}
                required
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Cc"
                  value={manualForm.cc}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, cc: e.target.value }))}
                  helperText={recipientsHelper}
                  fullWidth
                />
                <TextField
                  label="Bcc"
                  value={manualForm.bcc}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, bcc: e.target.value }))}
                  helperText={recipientsHelper}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Konu"
                value={manualForm.subject}
                onChange={(e) => setManualForm((prev) => ({ ...prev, subject: e.target.value }))}
                required
              />
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  İçerik
                </Typography>
                <RichTextEditor
                  value={manualForm.body}
                  onChange={(html) => setManualForm((prev) => ({ ...prev, body: html }))}
                  placeholder="E-posta içeriğini buraya yazın..."
                />
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={resetManualForm} disabled={sending}>
                  Temizle
                </Button>
                <Button variant="contained" onClick={handleSendManual} disabled={sending}>
                  {sending ? 'Gönderiliyor...' : 'E-Posta Gönder'}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <TextField
                label="Alıcılar (To)"
                value={templateForm.to}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, to: e.target.value }))}
                helperText={recipientsHelper}
                required
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Cc"
                  value={templateForm.cc}
                  onChange={(e) => setTemplateForm((prev) => ({ ...prev, cc: e.target.value }))}
                  helperText={recipientsHelper}
                  fullWidth
                />
                <TextField
                  label="Bcc"
                  value={templateForm.bcc}
                  onChange={(e) => setTemplateForm((prev) => ({ ...prev, bcc: e.target.value }))}
                  helperText={recipientsHelper}
                  fullWidth
                />
              </Stack>
              <Autocomplete
                options={templateOptions}
                loading={templateLoading}
                value={templateForm.template}
                onChange={(event, value) =>
                  setTemplateForm((prev) => ({ ...prev, template: value || null }))
                }
                onInputChange={(event, value) => setTemplateQuery(value)}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) =>
                  (option?.id || option?.Id) === (value?.id || value?.Id)
                }
                getOptionLabel={(option) => option?.name || option?.Name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="E-posta Şablonu"
                    placeholder="Şablon adı arayın"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {templateLoading ? <CircularProgress size={16} color="inherit" /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {templateForm.template?.subject && (
                <Typography variant="body2" color="text.secondary">
                  Şablon konusu: {templateForm.template.subject}
                </Typography>
              )}
              <TextField
                label="Konu Override"
                value={templateForm.subjectOverride}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, subjectOverride: e.target.value }))}
                helperText="Boş bırakılırsa şablonun orijinal konusu kullanılır."
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={resetTemplateForm} disabled={sending}>
                  Temizle
                </Button>
                <Button variant="contained" onClick={handleSendTemplate} disabled={sending}>
                  {sending ? 'Gönderiliyor...' : 'Şablon ile Gönder'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, gap: 2, flexWrap: 'wrap' }}
        >
          <Stack spacing={1} sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Gönderim Kayıtları
            </Typography>
            <TextField
              size="small"
              label="Ara"
              placeholder="Konu, alıcı veya kaynak..."
              value={search}
              onChange={(event) => {
                setPage(0);
                setSearch(event.target.value);
              }}
              sx={{ maxWidth: 360 }}
            />
          </Stack>
          <IconButton onClick={handleRefresh} disabled={recordsLoading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
        <Divider />
        {renderHistoryTable()}
        <Divider />
        <TablePagination
          component="div"
          count={recordsMeta.totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>
      <SentEmailDetailDialog
        open={detailOpen}
        onClose={closeDetail}
        record={detailRecord}
        template={detailTemplate}
        sender={detailSender}
        loading={detailLoading}
        error={detailError}
      />
    </Stack>
  );
};

export default EmailSendRecordsTab;

const SentEmailDetailDialog = ({ open, onClose, record, template, sender, loading, error }) => {
  if (!open || !record) return null;

  const failureReason =
    record.errorMessage ||
    record.ErrorMessage ||
    record.failureReason ||
    record.FailureReason ||
    null;

  const subject =
    resolveField(record, ['subject', 'Subject', 'subjectOverride', 'SubjectOverride']) || '—';
  const sentAt =
    resolveField(record, ['sentAt', 'SentAt', 'createdAt', 'CreatedAt', 'updatedAt', 'UpdatedAt']) || null;
  const source = resolveField(record, ['source', 'Source']) || '—';
  const sourceId = resolveField(record, ['sourceId', 'SourceId']) || null;
  const isHtml = resolveField(record, ['isHtml', 'IsHtml']);
  const toRecipients = resolveField(record, ['to', 'To']);
  const ccRecipients = resolveField(record, ['cc', 'Cc']);
  const bccRecipients = resolveField(record, ['bcc', 'Bcc']);
  const body = resolveField(record, ['body', 'Body']) || '';
  const placeholdersRaw = resolveField(record, ['placeholders', 'Placeholders']) || null;

  const formatRecipients = (value) => {
    if (!value) return '—';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
  };

  const placeholderEntries = placeholdersRaw
    ? Object.entries(placeholdersRaw).map(([key, val]) => ({
        key,
        value: typeof val === 'string' ? val : JSON.stringify(val),
      }))
    : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>E-posta Gönderim Detayı</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Konu
              </Typography>
              <Typography>{subject}</Typography>
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Alıcılar
              </Typography>
              <Typography variant="body2">
                <strong>To:</strong> {formatRecipients(toRecipients)}
              </Typography>
              <Typography variant="body2">
                <strong>Cc:</strong> {formatRecipients(ccRecipients)}
              </Typography>
              <Typography variant="body2">
                <strong>Bcc:</strong> {formatRecipients(bccRecipients)}
              </Typography>
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Gönderim Bilgileri
              </Typography>
              <Typography variant="body2">
                <strong>Gönderim Tarihi:</strong>{' '}
                {sentAt ? new Date(sentAt).toLocaleString('tr-TR') : '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Kaynak:</strong> {source}
                {sourceId ? ` (${sourceId})` : ''}
              </Typography>
              <Typography variant="body2">
                <strong>HTML İçerik:</strong> {isHtml ? 'Evet' : 'Hayır'}
              </Typography>
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Şablon
              </Typography>
              {record.templateId ? (
                template ? (
                  <Stack spacing={0.25}>
                    <Typography>
                      <strong>Adı:</strong> {template.name || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Konu:</strong> {template.subject || '—'}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Şablon bilgisi yükleniyor...
                  </Typography>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Şablon kullanılmadı.
                </Typography>
              )}
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Gönderen
              </Typography>
              {record.sentById ? (
                sender ? (
                  <Stack spacing={0.25}>
                    <Typography>
                      <strong>Kullanıcı Adı:</strong> {sender.username || sender.fullName || '—'}
                    </Typography>
                    {sender.email && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>E-posta:</strong> {sender.email}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Kullanıcı bilgisi yükleniyor...
                  </Typography>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Gönderen bilgisi mevcut değil.
                </Typography>
              )}
            </Stack>

            {placeholderEntries.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  Yer Tutucular
                </Typography>
                {placeholderEntries.map(({ key, value }) => (
                  <Typography key={key} variant="body2">
                    <strong>{`{{${key}}}`}</strong>: {value}
                  </Typography>
                ))}
              </Stack>
            )}

            {failureReason && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  Hata / Açıklama
                </Typography>
                <Typography variant="body2" color="error.main">
                  {failureReason}
                </Typography>
              </Stack>
            )}

            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="text.secondary">
                İçerik
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 320,
                  overflow: 'auto',
                  '& *': { maxWidth: '100%' },
                }}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

